/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

const MILLISECONDS_PER_HOUR = 60 * 60 * 1000;
const DEFAULT_MINIMUM_SAVINGS_EUR_PER_MWH = 10;
const DEFAULT_STABLE_SLOPE_TOLERANCE_EUR_PER_MWH_PER_HOUR = 1;
const DEFAULT_MAX_INTERESTING_TIMELINE_ITEMS = 7;

export interface ElectricityPricePoint {
    priceEurPerMwh: number;
    timestamp: Date;
}

export type ElectricityPriceLevel = 'cheap' | 'normal' | 'expensive';

export type ElectricityPriceTrendDirection = 'falling' | 'stable' | 'rising';

export type ElectricityPriceTrafficLight = 'green' | 'yellow' | 'red';

export type ElectricityPriceRecommendationAction =
    | 'consume-now'
    | 'consume-soon'
    | 'neutral'
    | 'wait'
    | 'avoid';

export type ElectricityPriceRecommendationReason =
    | 'price-cheap'
    | 'price-cheap-cheaper-soon'
    | 'price-expensive'
    | 'cheaper-soon'
    | 'much-cheaper-soon'
    | 'prices-rising'
    | 'best-price-now'
    | 'no-significant-benefit';

export interface ElectricityPriceThresholds {
    cheapBelow: number;
    expensiveAbove: number;
}

export interface ElectricityPriceTrend {
    direction: ElectricityPriceTrendDirection;
    slopeEurPerMwhPerHour: number;
}

export interface ElectricityPriceAssessment {
    action: ElectricityPriceRecommendationAction;
    offsetHours: number;
    priceEurPerMwh: number;
    priceLevel: ElectricityPriceLevel;
    timestamp: Date;
    trafficLight: ElectricityPriceTrafficLight;
    trend: ElectricityPriceTrendDirection;
}

export interface ElectricityPriceRecommendation {
    bestOption: {
        priceEurPerMwh: number;
        savingsEurPerMwh: number;
        timestamp: Date;
        waitHours: number;
    };
    current: {
        level: ElectricityPriceLevel;
        priceEurPerMwh: number;
        timestamp: Date;
    };
    horizon: {
        availableHours: number;
        maxWaitHours: number;
    };
    recommendation: {
        action: ElectricityPriceRecommendationAction;
        reason: ElectricityPriceRecommendationReason;
        trafficLight: ElectricityPriceTrafficLight;
    };
    trend: ElectricityPriceTrend;
}

export interface ElectricityPriceAdvisorOptions {
    maxWaitHours: number;
    minimumSavingsEurPerMwh?: number;
    prices: readonly ElectricityPricePoint[];
    stableSlopeToleranceEurPerMwhPerHour?: number;
    thresholds: ElectricityPriceThresholds;
}

export type ElectricityPriceAdvisorErrorCode =
    | 'invalid-configuration'
    | 'no-price-data'
    | 'no-current-price';

export class ElectricityPriceAdvisorError extends Error {
    public readonly code: ElectricityPriceAdvisorErrorCode;

    public constructor(code: ElectricityPriceAdvisorErrorCode, message: string) {
        super(message);
        this.name = 'ElectricityPriceAdvisorError';
        this.code = code;
    }
}

interface EvaluationContext {
    current: ElectricityPricePoint;
    timeline: ElectricityPricePoint[];
}

export class ElectricityPriceAdvisor {
    private readonly maxWaitHours: number;
    private readonly minimumSavingsEurPerMwh: number;
    private readonly prices: ElectricityPricePoint[];
    private readonly stableSlopeToleranceEurPerMwhPerHour: number;
    private readonly thresholds: ElectricityPriceThresholds;

    public constructor({
        maxWaitHours,
        minimumSavingsEurPerMwh = DEFAULT_MINIMUM_SAVINGS_EUR_PER_MWH,
        prices,
        stableSlopeToleranceEurPerMwhPerHour = DEFAULT_STABLE_SLOPE_TOLERANCE_EUR_PER_MWH_PER_HOUR,
        thresholds,
    }: ElectricityPriceAdvisorOptions) {
        if (
            !Number.isFinite(maxWaitHours) ||
            maxWaitHours < 0 ||
            !Number.isFinite(minimumSavingsEurPerMwh) ||
            minimumSavingsEurPerMwh < 0 ||
            !Number.isFinite(stableSlopeToleranceEurPerMwhPerHour) ||
            stableSlopeToleranceEurPerMwhPerHour < 0 ||
            !Number.isFinite(thresholds.cheapBelow) ||
            !Number.isFinite(thresholds.expensiveAbove) ||
            thresholds.cheapBelow > thresholds.expensiveAbove
        ) {
            throw new ElectricityPriceAdvisorError(
                'invalid-configuration',
                'Electricity price advisor configuration is invalid.',
            );
        }

        this.maxWaitHours = maxWaitHours;
        this.minimumSavingsEurPerMwh = minimumSavingsEurPerMwh;
        this.prices = ElectricityPriceAdvisor.normalizePrices(prices);
        this.stableSlopeToleranceEurPerMwhPerHour = stableSlopeToleranceEurPerMwhPerHour;
        this.thresholds = { ...thresholds };
    }

    public getRecommendation(now = new Date()): ElectricityPriceRecommendation {
        const { current, timeline } = this.getEvaluationContext(now);
        const bestOption = this.findBestPrice(timeline);
        const trend = this.calculateTrend(timeline);
        const savingsEurPerMwh = Math.max(0, current.priceEurPerMwh - bestOption.priceEurPerMwh);
        const waitHours = ElectricityPriceAdvisor.getHourOffset(current, bestOption);
        const level = this.getPriceLevel(current.priceEurPerMwh);

        return {
            bestOption: {
                priceEurPerMwh: bestOption.priceEurPerMwh,
                savingsEurPerMwh,
                timestamp: new Date(bestOption.timestamp),
                waitHours,
            },
            current: {
                level,
                priceEurPerMwh: current.priceEurPerMwh,
                timestamp: new Date(current.timestamp),
            },
            horizon: {
                availableHours: ElectricityPriceAdvisor.getHourOffset(
                    current,
                    timeline[timeline.length - 1],
                ),
                maxWaitHours: this.maxWaitHours,
            },
            recommendation: this.resolveRecommendation(
                level,
                trend.direction,
                waitHours,
                savingsEurPerMwh,
            ),
            trend,
        };
    }

    public getTimeline(now = new Date()): ElectricityPriceAssessment[] {
        const { current, timeline } = this.getEvaluationContext(now);
        const bestOption = this.findBestPrice(timeline);

        return timeline.map((point, index) => {
            const pointSavings = Math.max(0, point.priceEurPerMwh - bestOption.priceEurPerMwh);
            const isBestOption = point.priceEurPerMwh === bestOption.priceEurPerMwh;
            const level = this.getPriceLevel(point.priceEurPerMwh);
            // Timeline cards describe the next price movement, not the overall horizon trend.
            const trend = this.calculateTrend(timeline.slice(index, index + 2)).direction;
            const recommendation = this.resolveTimelineRecommendation(
                level,
                isBestOption,
                pointSavings,
            );

            return {
                ...recommendation,
                offsetHours: ElectricityPriceAdvisor.getHourOffset(current, point),
                priceEurPerMwh: point.priceEurPerMwh,
                priceLevel: level,
                timestamp: new Date(point.timestamp),
                trend,
            };
        });
    }

    public getInterestingTimeline(
        now = new Date(),
        maxItems = DEFAULT_MAX_INTERESTING_TIMELINE_ITEMS,
    ): ElectricityPriceAssessment[] {
        if (!Number.isInteger(maxItems) || maxItems < 1) {
            throw new ElectricityPriceAdvisorError(
                'invalid-configuration',
                'The maximum number of timeline items must be a positive integer.',
            );
        }

        const timeline = this.getTimeline(now);

        if (timeline.length <= maxItems) {
            return timeline;
        }

        const candidates = new Map<number, number>();
        const addCandidate = (index: number, priority: number) => {
            candidates.set(index, Math.max(candidates.get(index) ?? 0, priority));
        };
        const cheapestIndex = timeline.reduce(
            (bestIndex, assessment, index) => (
                assessment.priceEurPerMwh < timeline[bestIndex].priceEurPerMwh
                    ? index
                    : bestIndex
            ),
            0,
        );
        const mostExpensiveIndex = timeline.reduce(
            (bestIndex, assessment, index) => (
                assessment.priceEurPerMwh > timeline[bestIndex].priceEurPerMwh
                    ? index
                    : bestIndex
            ),
            0,
        );

        addCandidate(0, 10_000);
        addCandidate(cheapestIndex, 9_000);
        addCandidate(mostExpensiveIndex, 8_000);
        addCandidate(timeline.length - 1, 7_000);

        timeline.forEach((assessment, index) => {
            if (index === 0) {
                return;
            }

            const previous = timeline[index - 1];
            const priceDifference = Math.abs(
                assessment.priceEurPerMwh - previous.priceEurPerMwh,
            );

            if (assessment.trafficLight !== previous.trafficLight) {
                addCandidate(index, 6_000 + priceDifference);
            }

            if (assessment.priceLevel !== previous.priceLevel) {
                addCandidate(index, 5_000 + priceDifference);
            }

            if (index < timeline.length - 1) {
                const next = timeline[index + 1];
                const isLocalMinimum =
                    assessment.priceEurPerMwh < previous.priceEurPerMwh &&
                    assessment.priceEurPerMwh < next.priceEurPerMwh;
                const isLocalMaximum =
                    assessment.priceEurPerMwh > previous.priceEurPerMwh &&
                    assessment.priceEurPerMwh > next.priceEurPerMwh;

                if (isLocalMinimum || isLocalMaximum) {
                    const prominence = Math.min(
                        Math.abs(assessment.priceEurPerMwh - previous.priceEurPerMwh),
                        Math.abs(assessment.priceEurPerMwh - next.priceEurPerMwh),
                    );

                    addCandidate(index, 4_000 + prominence);
                }
            }
        });

        return [...candidates.entries()]
            .sort(([leftIndex, leftPriority], [rightIndex, rightPriority]) => (
                rightPriority - leftPriority || leftIndex - rightIndex
            ))
            .slice(0, maxItems)
            .map(([index]) => timeline[index])
            .sort((left, right) => left.timestamp.getTime() - right.timestamp.getTime());
    }

    public getBestConsumptionTime(now = new Date()): ElectricityPricePoint {
        const bestOption = this.findBestPrice(this.getEvaluationContext(now).timeline);

        return ElectricityPriceAdvisor.clonePoint(bestOption);
    }

    public getPriceLevel(priceEurPerMwh: number): ElectricityPriceLevel {
        if (priceEurPerMwh < this.thresholds.cheapBelow) {
            return 'cheap';
        }

        if (priceEurPerMwh > this.thresholds.expensiveAbove) {
            return 'expensive';
        }

        return 'normal';
    }

    public getTrend(now = new Date()): ElectricityPriceTrend {
        return this.calculateTrend(this.getEvaluationContext(now).timeline);
    }

    private static clonePoint(point: ElectricityPricePoint): ElectricityPricePoint {
        return {
            priceEurPerMwh: point.priceEurPerMwh,
            timestamp: new Date(point.timestamp),
        };
    }

    private static getHourOffset(
        reference: ElectricityPricePoint,
        point: ElectricityPricePoint,
    ): number {
        return (point.timestamp.getTime() - reference.timestamp.getTime()) / MILLISECONDS_PER_HOUR;
    }

    private static normalizePrices(prices: readonly ElectricityPricePoint[]): ElectricityPricePoint[] {
        const pricesByTimestamp = new Map<number, ElectricityPricePoint>();

        prices.forEach((point) => {
            const timestamp = point.timestamp instanceof Date
                ? point.timestamp.getTime()
                : Number.NaN;

            if (!Number.isFinite(timestamp) || !Number.isFinite(point.priceEurPerMwh)) {
                return;
            }

            pricesByTimestamp.set(timestamp, ElectricityPriceAdvisor.clonePoint(point));
        });

        return [...pricesByTimestamp.values()].sort(
            (left, right) => left.timestamp.getTime() - right.timestamp.getTime(),
        );
    }

    private calculateTrend(timeline: readonly ElectricityPricePoint[]): ElectricityPriceTrend {
        if (timeline.length < 2) {
            return {
                direction: 'stable',
                slopeEurPerMwhPerHour: 0,
            };
        }

        const firstTimestamp = timeline[0].timestamp.getTime();
        const coordinates = timeline.map((point) => ({
            x: (point.timestamp.getTime() - firstTimestamp) / MILLISECONDS_PER_HOUR,
            y: point.priceEurPerMwh,
        }));
        const meanX = coordinates.reduce((sum, point) => sum + point.x, 0) / coordinates.length;
        const meanY = coordinates.reduce((sum, point) => sum + point.y, 0) / coordinates.length;
        const numerator = coordinates.reduce(
            (sum, point) => sum + ((point.x - meanX) * (point.y - meanY)),
            0,
        );
        const denominator = coordinates.reduce(
            (sum, point) => sum + ((point.x - meanX) ** 2),
            0,
        );
        const slopeEurPerMwhPerHour = denominator === 0 ? 0 : numerator / denominator;
        const direction: ElectricityPriceTrendDirection =
            Math.abs(slopeEurPerMwhPerHour) <= this.stableSlopeToleranceEurPerMwhPerHour
                ? 'stable'
                : slopeEurPerMwhPerHour < 0
                    ? 'falling'
                    : 'rising';

        return {
            direction,
            slopeEurPerMwhPerHour,
        };
    }

    private findBestPrice(timeline: readonly ElectricityPricePoint[]): ElectricityPricePoint {
        return timeline.reduce((best, point) => (
            point.priceEurPerMwh < best.priceEurPerMwh ? point : best
        ));
    }

    private getEvaluationContext(now: Date): EvaluationContext {
        if (this.prices.length === 0) {
            throw new ElectricityPriceAdvisorError(
                'no-price-data',
                'No valid electricity price data is available.',
            );
        }

        const nowTimestamp = now.getTime();

        if (!Number.isFinite(nowTimestamp)) {
            throw new ElectricityPriceAdvisorError(
                'no-current-price',
                'The reference time is invalid.',
            );
        }

        let current: ElectricityPricePoint | undefined;

        for (let index = this.prices.length - 1; index >= 0; index -= 1) {
            if (this.prices[index].timestamp.getTime() <= nowTimestamp) {
                current = this.prices[index];
                break;
            }
        }

        if (!current) {
            throw new ElectricityPriceAdvisorError(
                'no-current-price',
                'No electricity price slot is available for the reference time.',
            );
        }

        if (nowTimestamp >= current.timestamp.getTime() + MILLISECONDS_PER_HOUR) {
            throw new ElectricityPriceAdvisorError(
                'no-current-price',
                'The latest electricity price slot has already expired.',
            );
        }

        const horizonEnd = current.timestamp.getTime() + (this.maxWaitHours * MILLISECONDS_PER_HOUR);
        const timeline = this.prices.filter((point) => (
            point.timestamp.getTime() >= current.timestamp.getTime() &&
            point.timestamp.getTime() <= horizonEnd
        ));

        return { current, timeline };
    }

    private resolveRecommendation(
        level: ElectricityPriceLevel,
        trend: ElectricityPriceTrendDirection,
        waitHours: number,
        savingsEurPerMwh: number,
    ): ElectricityPriceRecommendation['recommendation'] {
        if (level === 'cheap') {
            return {
                action: 'consume-now',
                reason: waitHours > 0 && savingsEurPerMwh >= this.minimumSavingsEurPerMwh
                    ? 'price-cheap-cheaper-soon'
                    : 'price-cheap',
                trafficLight: 'green',
            };
        }

        if (waitHours > 0 && savingsEurPerMwh >= this.minimumSavingsEurPerMwh) {
            return {
                action: 'wait',
                reason: savingsEurPerMwh >= this.minimumSavingsEurPerMwh * 2
                    ? 'much-cheaper-soon'
                    : 'cheaper-soon',
                trafficLight: 'red',
            };
        }

        if (waitHours === 0 && trend === 'rising') {
            return {
                action: 'consume-now',
                reason: 'prices-rising',
                trafficLight: 'green',
            };
        }

        if (level === 'expensive') {
            return {
                action: 'avoid',
                reason: 'price-expensive',
                trafficLight: 'red',
            };
        }

        if (waitHours === 0) {
            return {
                action: 'consume-soon',
                reason: 'best-price-now',
                trafficLight: 'yellow',
            };
        }

        return {
            action: 'neutral',
            reason: 'no-significant-benefit',
            trafficLight: 'yellow',
        };
    }

    private resolveTimelineRecommendation(
        level: ElectricityPriceLevel,
        isBestOption: boolean,
        savingsEurPerMwh: number,
    ): Pick<ElectricityPriceAssessment, 'action' | 'trafficLight'> {
        if (isBestOption || level === 'cheap') {
            return {
                action: 'consume-now',
                trafficLight: 'green',
            };
        }

        if (savingsEurPerMwh >= this.minimumSavingsEurPerMwh) {
            return {
                action: 'wait',
                trafficLight: 'red',
            };
        }

        return {
            action: 'neutral',
            trafficLight: 'yellow',
        };
    }
}
